import React, { useEffect, useState } from "react";

import addIcon from '../../../assets/icons/add.svg';
import deleteIcon from '../../../assets/icons/delete.svg';
import warning_icon from '../../../assets/icons/WarningCircle.svg';

import FloatingLabelInput from "../../../common/components/FloatingLabelInput";
import { getCountries } from "../../../api/location/getCountries";
import { getRegions } from "../../../api/location/getRegions";
import { getDistricts } from "../../../api/location/getDistricts";
import { getLocalizedName } from "../../../api/location/locationUtils";

import { t } from "i18next";
import { useTranslation } from "react-i18next";

export function ContactInfo({
    userData = {},
    additionalPhones = [],
    phoneErrors = [],
    handlePhoneChange = () => { },
    handleDeletePhone = () => { },
    handleAddPhone = () => { },
    UserLocations = [],
    selectedLocation = null,
    handleSelectLocation = () => { },
    isSubmitted = false,
}) {
    const { i18n } = useTranslation();
    const currentLanguage = i18n.language;
    const [countries, setCountries] = useState([]);
    const [regions, setRegions] = useState([]);
    const [districts, setDistricts] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [selectedRegion, setSelectedRegion] = useState(null);
    const [selectedDistrict, setSelectedDistrict] = useState(null);

    useEffect(() => {
        const loadCountries = async () => {
            try {
                const data = await getCountries();
                setCountries(data);
            } catch (error) {
                console.error("Failed to load countries for ContactInfo:", error);
            }
        };

        loadCountries();
    }, []);

    const handleCountryChange = async (e) => {
        const value = e.target.value;
        if (!value) {
            setSelectedCountry(null);
            setSelectedRegion(null);
            setSelectedDistrict(null);
            setRegions([]);
            setDistricts([]);
            return;
        }

        const id = parseInt(value, 10);
        const country = countries.find((c) => c.id === id) || null;
        setSelectedCountry(country);
        setSelectedRegion(null);
        setSelectedDistrict(null);
        setRegions([]);
        setDistricts([]);

        if (country) {
        try {
                const regs = await getRegions(country.id);
                setRegions(regs);
            } catch (error) {
                console.error("Failed to load regions for ContactInfo:", error);
            }
        }
    };

    const handleRegionChange = async (e) => {
        const value = e.target.value;
        if (!value) {
            setSelectedRegion(null);
            setSelectedDistrict(null);
            setDistricts([]);
            return;
        }

        const id = parseInt(value, 10);
        const region = regions.find((r) => r.id === id) || null;
        setSelectedRegion(region);
        setSelectedDistrict(null);
        setDistricts([]);

        if (region) {
            try {
                const dists = await getDistricts(region.id);
                setDistricts(dists);
            } catch (error) {
                console.error("Failed to load districts for ContactInfo:", error);
            }
        }
    };

    const handleDistrictChange = (e) => {
        const value = e.target.value;
        if (!value) {
            setSelectedDistrict(null);
            handleSelectLocation(null);
            return;
        }

        const id = parseInt(value, 10);
        const district = districts.find((d) => d.id === id) || null;
        setSelectedDistrict(district);

        if (district) {
            const syntheticLocation = {
                country_id: selectedCountry?.id || null,
                region_id: selectedRegion?.id || null,
                district_id: district.id,
                country: getLocalizedName(selectedCountry, currentLanguage),
                province: getLocalizedName(selectedRegion, currentLanguage),
                locality: getLocalizedName(district, currentLanguage),
            };
            handleSelectLocation(syntheticLocation);
        }
    };

    const hasAddress = !!(selectedCountry || selectedRegion || selectedDistrict || selectedLocation);

    return <div className="w-full lg:w-[35%] h-min bg-white rounded-[10px] p-5 box-border mb-5 lg:mb-0">
        <h4 className="text-xl font-bold text-[#222] p-0 m-0 mb-0 bg-transparent">{t('your_data')}</h4>

        <div className="contact_section">
            <label className="text_input_label">{t('contact_name')}</label>
            <FloatingLabelInput
                label={t('nameLabel')}
                value={userData?.name || t('default_value')}
                readOnly={true}
            />
        </div>

        <div className="phone_section">
            <label className="text_input_label">{t('phone')}</label>
            <FloatingLabelInput
                value={userData?.phone_number || t('default_value')}
                type="tel"
                readOnly={true}
            />

            {additionalPhones?.map((phone, index) => (
                <div key={index} className="flex flex-row items-center relative">
                    <FloatingLabelInput
                        label={t('enter_additional_phone')}
                        value={phone}
                        type="tel"
                        onChange={(e) => handlePhoneChange(index, e.target.value)}
                        className={phoneErrors[index] ? 'error' : ''}
                    />
                    <img src={deleteIcon} alt={t('delete')} className="click absolute right-2.5 top-[38%] -translate-y-1/2 cursor-pointer" onClick={() => handleDeletePhone(index)} />
                    {phoneErrors[index] && (
                        <span className="error_text">{t('enter_phone')}</span>
                    )}
                </div>
            ))}

            <button className="text-[var(--primary-color)] bg-transparent text-sm border-none w-full flex items-center justify-center py-2">
                <img src={addIcon} alt={t('add')} className="mr-1.5" /> <span className="text-[var(--text-color)]">{t('add_more')}</span>
            </button>
        </div>

        <hr className="divider" style={{ marginTop: 14, marginBottom: 14 }} />

        <div className="address_section">
            <p style={{ marginBottom: 14 }} className="text_input_label">{t('your_address')}</p>
            <div className="input_header">
                {isSubmitted && !hasAddress && (
                    <>
                        <span className="error_text">{t('required_text')}</span>
                        <img src={warning_icon} alt="warning" />
                    </>
                )}
            </div>

            {/* Location dropdowns: country, region, district */}
            <div className="space-y-3 mt-3">
                {/* Country */}
                <div>
                    <label className="text_input_label">{t('country')}</label>
                    <select
                        className="input_field"
                        value={selectedCountry?.id || ""}
                        onChange={handleCountryChange}
                    >
                        <option value="">{t('select_country')}</option>
                        {countries.map((country) => (
                            <option key={country.id} value={country.id}>
                                {getLocalizedName(country, currentLanguage)}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Region */}
                <div>
                    <label className="text_input_label">{t('region')}</label>
                    <select
                        className="input_field"
                        value={selectedRegion?.id || ""}
                        onChange={handleRegionChange}
                        disabled={!selectedCountry}
                    >
                        <option value="">{t('select_region')}</option>
                        {regions.map((region) => (
                            <option key={region.id} value={region.id}>
                                {getLocalizedName(region, currentLanguage)}
                            </option>
                        ))}
                    </select>
                </div>

                {/* District */}
                <div>
                    <label className="text_input_label">{t('district')}</label>
                    <select
                        className="input_field"
                        value={selectedDistrict?.id || ""}
                        onChange={handleDistrictChange}
                        disabled={!selectedRegion}
                    >
                        <option value="">{t('select_district')}</option>
                        {districts.map((district) => (
                            <option key={district.id} value={district.id}>
                                {getLocalizedName(district, currentLanguage)}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

        </div>
    </div>;
}